import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '../lib/utils';
import { CheckerInBoxIcon } from './bs-icons';

const SquareInBoxIcon = () => {
  // size 8, border 1px svg
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      // fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="7" height="7" rx="1" fill="#0265ff" />
    </svg>
  );
};

export type SquareCheckerProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  /** Optional label rendered beside the checkbox */
  children?: React.ReactNode;
  readonly?: boolean;
  intermediate?: boolean;
};

export const SquareChecker = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  SquareCheckerProps
>(({ className, children, id, readonly, disabled, intermediate, ...props }, ref) => {
  const generatedId = React.useId();
  const checkboxId = id ?? generatedId;

  return (
    <label
      htmlFor={checkboxId}
      className={cn(
        'inline-flex items-center gap-[6px] select-none text-[#121111] text-[14px]',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        {
          'pointer-events-none': readonly,
        }
      )}
    >
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        disabled={disabled}
        className={cn(
          // base layout
          'inline-flex shrink-0 items-center justify-center',
          'w-[16px] h-[16px] rounded-[2px] box-border border',
          'transition-colors duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0265FF] focus-visible:ring-offset-1',
          // normal: gray border, white bg
          'border-[#E0E0E0] bg-white',
          // hover: blue border (browsers suppress hover on disabled <button>)
          'hover:border-[#0265FF]',
          // checked: blue fill
          'data-[state=checked]:bg-[#0265FF] data-[state=checked]:border-[#0265FF]',
          // disabled unchecked: gray bg + gray border
          'disabled:cursor-not-allowed disabled:bg-[#F0F0F0] disabled:border-[#C2C2C2]',
          // disabled checked: use muted gray fill
          'disabled:data-[state=checked]:bg-[#C2C2C2] disabled:data-[state=checked]:border-[#C2C2C2]',
          // indeterminate: blue fill
          intermediate && 'bg-white border-[#0265FF] text-[#0265FF]',
          className,
          {
            'opacity-50': readonly,
          }
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="text-white" forceMount={intermediate || undefined}>
          {intermediate ? <SquareInBoxIcon /> : <CheckerInBoxIcon />}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      {children != null && (
        <span className={cn('text-sm leading-none', disabled && 'text-[#C2C2C2]')}>{children}</span>
      )}
    </label>
  );
});

SquareChecker.displayName = 'SquareChecker';
